import React, { useState, useRef, useCallback } from 'react';

const ImageUploader = ({ onImageUpload, onClose, isOpen, canvasDimensions = { width: 600, height: 600 } }) => {
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef();

  // Configuration for image processing
  const uploadConfig = {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxDimensions: { width: 2000, height: 2000 },
    quality: 0.8
  };

  // Calculate optimal dimensions for image resizing
  const calculateOptimalDimensions = (originalWidth, originalHeight, maxDimensions) => {
    const { width: maxW, height: maxH } = maxDimensions;
    
    if (originalWidth <= maxW && originalHeight <= maxH) {
      return { width: originalWidth, height: originalHeight };
    }
    
    const ratio = Math.min(maxW / originalWidth, maxH / originalHeight);
    return {
      width: Math.round(originalWidth * ratio),
      height: Math.round(originalHeight * ratio)
    };
  };

  // Calculate canvas-appropriate dimensions (50% of canvas size or smaller)
  const calculateCanvasDimensions = (originalWidth, originalHeight, canvasWidth, canvasHeight) => {
    // Target size is 50% of canvas height, maintaining aspect ratio
    const targetHeight = canvasHeight * 0.5;
    const targetWidth = canvasWidth * 0.5;
    
    // Calculate scale factor to fit within target dimensions
    const scaleX = targetWidth / originalWidth;
    const scaleY = targetHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
    
    return {
      width: Math.round(originalWidth * scale),
      height: Math.round(originalHeight * scale)
    };
  };

  // Process and optimize uploaded image
  const processAndUploadImage = useCallback(async (file) => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      
      // Validate file type
      if (!uploadConfig.allowedTypes.includes(file.type)) {
        throw new Error(`Unsupported file type. Please use: ${uploadConfig.allowedTypes.join(', ')}`);
      }
      
      // Validate file size
      if (file.size > uploadConfig.maxFileSize) {
        throw new Error(`File too large. Maximum size is ${Math.round(uploadConfig.maxFileSize / 1024 / 1024)}MB`);
      }

      setUploadProgress(25);

      // Create image element for processing
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Load and process image
      const processedImage = await new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            setUploadProgress(50);
            
            // First, calculate optimal dimensions for file size
            const optimalDims = calculateOptimalDimensions(
              img.width, 
              img.height, 
              uploadConfig.maxDimensions
            );
            
            // Then, calculate canvas-appropriate dimensions (50% of canvas size)
            const canvasDims = calculateCanvasDimensions(
              optimalDims.width,
              optimalDims.height,
              canvasDimensions.width,
              canvasDimensions.height
            );
            
            // Use the smaller of the two dimensions
            const finalWidth = Math.min(optimalDims.width, canvasDims.width);
            const finalHeight = Math.min(optimalDims.height, canvasDims.height);
            
            // Resize to final dimensions
            canvas.width = finalWidth;
            canvas.height = finalHeight;
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);
            
            setUploadProgress(75);
            
            // Convert to blob with quality settings
            canvas.toBlob(resolve, 'image/jpeg', uploadConfig.quality);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
      });

      setUploadProgress(90);

      // Create asset data
      const imageAsset = {
        id: `uploaded_${Date.now()}`,
        src: URL.createObjectURL(processedImage),
        originalFile: file,
        width: canvas.width,
        height: canvas.height,
        type: 'uploadedImage',
        scaling: 100, // Use 100% since we've already sized it appropriately
        name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        description: `Uploaded image: ${file.name}`,
        tags: ['uploaded', 'image']
      };

      setUploadProgress(100);
      
      // Add to uploaded images
      onImageUpload(imageAsset);
      
      // Close modal after successful upload
      setTimeout(() => {
        onClose();
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      console.error('Image processing failed:', error);
      setError(error.message);
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [onImageUpload, onClose, canvasDimensions.width, canvasDimensions.height, uploadConfig.maxDimensions, uploadConfig.quality, uploadConfig.maxFileSize, uploadConfig.allowedTypes]);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processAndUploadImage(files[0]);
    }
  }, [processAndUploadImage]);

  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => 
      uploadConfig.allowedTypes.includes(file.type)
    );
    
    if (imageFiles.length > 0) {
      processAndUploadImage(imageFiles[0]);
    } else {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
    }
  }, [processAndUploadImage, uploadConfig.allowedTypes]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Upload Image</h3>
          <button 
            onClick={onClose} 
            className="close-button"
            disabled={isUploading}
          >
            &times;
          </button>
        </div>
        
        <div className="modal-content">
          <div 
            className={`upload-zone ${dragActive ? 'drag-active' : ''} ${isUploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={isUploading}
            />
            
            <div className="upload-content">
              {isUploading ? (
                <div className="upload-progress">
                  <div className="progress-icon">⏳</div>
                  <div className="progress-text">Processing image...</div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="progress-percentage">{uploadProgress}%</div>
                </div>
              ) : (
                <>
                  <div className="upload-icon">📷</div>
                  <div className="upload-text">
                    <p>Drag & drop an image here</p>
                    <p>or click to browse</p>
                  </div>
                  <button 
                    className="browse-button"
                    disabled={isUploading}
                  >
                    Choose File
                  </button>
                </>
              )}
            </div>
          </div>
          
          <div className="upload-info">
            <p><strong>Supported formats:</strong> JPG, PNG, GIF, WebP</p>
            <p><strong>Maximum file size:</strong> 10MB</p>
            <p><strong>Images will be automatically sized to fit the canvas</strong></p>
            <p><strong>Target size:</strong> 50% of canvas dimensions</p>
          </div>
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button 
                onClick={() => setError(null)}
                className="dismiss-error"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
